import { ApiError } from '@/lib/api/api-error'
import { inventoryEndpoints, mainEndpoints } from '@/lib/api/endpoints'
import { blobError, downloadBlob } from '@/lib/api/download'
import { inventoryApi, mainApi, mainHttp } from '@/lib/api/http'

import { ProductBranchLinkMode } from '../types'
import type {
  ChoiceGroupSummary,
  PageResult,
  ProductBranchLinkBody,
  ProductBrand,
  ProductCategory,
  ProductDetail,
  ProductImportResult,
  ProductInventoryRef,
  ProductInventoryRetryResult,
  ProductListQuery,
  ProductSummary,
  OptionSetSummary,
  SaveProductBody,
} from '../types'

/* -------------------------------------------------------------------------- */
/* Products                                                                    */
/* -------------------------------------------------------------------------- */

export async function fetchProducts(query: ProductListQuery): Promise<PageResult<ProductSummary>> {
  const { categoryIds, brandIds, ...rest } = query

  return await mainApi.get<PageResult<ProductSummary>>(mainEndpoints.products.root, {
    params: {
      ...rest,
      // An empty list is dropped rather than sent. The repeated-key form Nest
      // needs is set on the client — see `paramsSerializer` in `http.ts`.
      ...(categoryIds?.length ? { categoryIds } : {}),
      ...(brandIds?.length ? { brandIds } : {}),
    },
  })
}

export async function fetchProduct(id: string): Promise<ProductDetail> {
  return await mainApi.get<ProductDetail>(`${mainEndpoints.products.root}/${id}`)
}

/**
 * Creates or updates, depending on whether the body carries an `id` — one endpoint
 * serves both.
 *
 * Every variant the product should keep has to be in `productVariants`: the server
 * deletes the children that are missing from it. `fetchProduct` returns them under
 * `children`, so an edit that leaves the variants alone still has to send them back.
 */
export async function saveProduct(body: SaveProductBody): Promise<ProductDetail> {
  return await mainApi.put<ProductDetail>(mainEndpoints.products.root, body)
}

export async function deleteProduct(id: string): Promise<void> {
  await mainApi.delete<void>(`${mainEndpoints.products.root}/${id}`)
}

/** Whether the product is sellable at all. Disabled hides it everywhere. */
export async function setProductEnabled(id: string, enabled: boolean): Promise<void> {
  await mainApi.put<void>(`${mainEndpoints.products.root}/${id}/${enabled ? 'enable' : 'disable'}`)
}

/** Whether the product is on the storefront. A product can be enabled but unpublished. */
export async function setProductPublished(id: string, published: boolean): Promise<void> {
  await mainApi.post<void>(
    `${mainEndpoints.products.root}/${id}/${published ? 'publish' : 'unpublish'}`,
  )
}

/* -------------------------------------------------------------------------- */
/* Inventory link                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Whether inventory has a stock item for this row of a product.
 *
 * Asked of the inventory service directly, not through `features/inventory`:
 * that feature imports this one, and the edge back would make the two barrels
 * circular. A 404 is the answer "no"; anything else wrong is thrown.
 */
export async function isTrackedInInventory(ref: ProductInventoryRef): Promise<boolean> {
  const root =
    ref.kind === 'variant' ? inventoryEndpoints.itemsByVariant : inventoryEndpoints.itemsByProduct

  try {
    await inventoryApi.get<unknown>(`${root}/${encodeURIComponent(ref.id)}`)
    return true
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return false
    throw error
  }
}

/**
 * Sends a product to inventory again, for one whose save did not reach it.
 *
 * Sent is not created: inventory picks it up from a stream in its own time, so
 * the answer only says what went, and `isTrackedInInventory` says what took.
 */
export async function retryProductInventory(id: string): Promise<ProductInventoryRetryResult> {
  return await mainApi.post<ProductInventoryRetryResult>(
    `${mainEndpoints.products.root}/${id}/inventory/retry`,
  )
}

/* -------------------------------------------------------------------------- */
/* Bulk branch links                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Puts these products on these outlets, or takes them off.
 *
 * Linking skips a pair that already exists, so the stock counted there
 * survives. Unlinking drops that stock with the link, and is refused outright
 * — nothing written — when it would leave any of the products at no branch.
 */
export async function setProductBranches(
  mode: ProductBranchLinkMode,
  body: ProductBranchLinkBody,
): Promise<void> {
  const url =
    mode === ProductBranchLinkMode.Link
      ? mainEndpoints.products.linkBranches
      : mainEndpoints.products.unlinkBranches

  await mainApi.put<boolean>(url, body)
}

/**
 * Makes these exactly the branches one product is sold at — the "By product"
 * editor's save.
 *
 * Replaces, unlike `setProductBranches`. Branches the product keeps keep their
 * stock count; only the ones dropped from the list lose theirs. The server
 * takes it as one transaction and refuses an empty list.
 */
export async function setProductBranchList(id: string, tenantBranchIds: string[]): Promise<void> {
  await mainApi.put<boolean>(`${mainEndpoints.products.root}/${id}/branches`, { tenantBranchIds })
}

/* -------------------------------------------------------------------------- */
/* Bulk import and export                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The blank workbook, and the catalogue in that same workbook.
 *
 * One template for both directions is the point of the feature: what an export
 * writes is what an import reads, so correcting four hundred prices is a
 * download, a find-and-replace and an upload rather than four hundred dialogs.
 *
 * Both bypass `mainApi` — the endpoints answer with a spreadsheet, and the JSON
 * client would decode the bytes as text and lose them.
 */
export async function downloadProductTemplate(): Promise<void> {
  await downloadWorkbook(mainEndpoints.products.exportTemplate, {}, 'product-import-template.xlsx')
}

/**
 * Exports what the current filters select, not the current page.
 *
 * `limit` and `offset` are deliberately dropped: the export endpoint takes the
 * five filters and no paging, so the file always holds everything the list
 * would eventually show rather than the ten rows on screen.
 *
 * The outlet filter travels with the rest. A workbook that quietly held every
 * branch's products while the screen showed one would be edited and sent back,
 * and the import would apply it.
 */
export async function exportProducts(query: ProductListQuery): Promise<void> {
  const { limit: _limit, offset: _offset, categoryIds, brandIds, ...filters } = query

  await downloadWorkbook(
    mainEndpoints.products.export,
    {
      ...filters,
      ...(categoryIds?.length ? { categoryIds } : {}),
      ...(brandIds?.length ? { brandIds } : {}),
    },
    'products.xlsx',
  )
}

async function downloadWorkbook(url: string, params: Record<string, unknown>, fileName: string) {
  let data: Blob

  try {
    ;({ data } = await mainHttp.get<Blob>(url, {
      params,
      responseType: 'blob',
    }))
  } catch (error) {
    throw await blobError(error)
  }

  downloadBlob(data, fileName)
}

/**
 * Checks a filled-in workbook without writing anything.
 *
 * The merchant sees what the import would do before it does it, which for a
 * four-hundred-row file is the difference between a confident upload and a
 * nervous one.
 */
export async function validateProductImport(file: File): Promise<ProductImportResult> {
  return await postWorkbook(mainEndpoints.products.importValidate, file)
}

/**
 * Imports the workbook.
 *
 * A file that fails validation comes back as a normal 200 with `applied` false
 * and the errors listed — not as a thrown error — because a per-row report is
 * the answer here, and flattening it into one message would throw away the only
 * part the merchant can act on. Only a file that is not a workbook at all, or a
 * request that never landed, rejects.
 */
export async function importProducts(file: File): Promise<ProductImportResult> {
  return await postWorkbook(mainEndpoints.products.import, file)
}

function postWorkbook(url: string, file: File): Promise<ProductImportResult> {
  const body = new FormData()

  body.append('file', file)

  /*
   * The Content-Type has to be overridden, and `multipart/form-data` without a
   * boundary is the right thing to write.
   *
   * Both clients default to `application/json`, and axios reads that default in
   * `transformRequest` *before* its adapter looks at the body: seeing a JSON
   * content type it runs the FormData through `formDataToJSON` and posts a JSON
   * object, which arrives at Nest as a request with no file part at all.
   * Naming multipart here stops that, and the adapter then clears the header
   * anyway so the browser can set it with the boundary only it knows.
   */
  return mainApi.post<ProductImportResult>(url, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/* -------------------------------------------------------------------------- */
/* Supporting catalogue                                                        */
/* -------------------------------------------------------------------------- */

/** Everything the category picker and the list filter need. One page covers a tenant. */
export async function fetchCategories(): Promise<ProductCategory[]> {
  const page = await mainApi.get<PageResult<ProductCategory>>(mainEndpoints.categories, {
    params: { limit: CATALOGUE_PAGE_LIMIT, offset: 0 },
  })

  return page.rows ?? []
}

/**
 * Every brand the tenant has, active or not, for the form's picker and the
 * list's filter.
 *
 * Paged through rather than read as one page like categories: a shop that
 * stocks other people's goods can carry a few hundred brands, and a picker cut
 * off at the hundredth would hide the rest without a word. Inactive ones come
 * too — the filter still has to find products that carry one.
 */
export async function fetchBrands(): Promise<ProductBrand[]> {
  const brands: ProductBrand[] = []

  for (let page = 0; page < BRAND_PAGES_MAX; page += 1) {
    const { rows = [], count } = await mainApi.get<PageResult<ProductBrand>>(mainEndpoints.brands, {
      params: { limit: CATALOGUE_PAGE_LIMIT, offset: page * CATALOGUE_PAGE_LIMIT },
    })

    brands.push(...rows)
    if (rows.length < CATALOGUE_PAGE_LIMIT || brands.length >= count) break
  }

  return brands
}

/** A ceiling on the loop above, so a server that miscounts cannot spin it forever. */
const BRAND_PAGES_MAX = 20

export async function fetchChoiceGroups(): Promise<ChoiceGroupSummary[]> {
  const page = await mainApi.get<PageResult<ChoiceGroupSummary>>(mainEndpoints.choiceGroups, {
    params: { limit: CATALOGUE_PAGE_LIMIT, offset: 0 },
  })

  return page.rows ?? []
}

/**
 * The tenant's saved option axes, for the form's "add from option set" picker.
 *
 * The endpoint is documented as returning a single set but the service pages, so the
 * envelope is unwrapped defensively — a bare array would still read.
 */
export async function fetchOptionSets(): Promise<OptionSetSummary[]> {
  const page = await mainApi.get<PageResult<OptionSetSummary> | OptionSetSummary[]>(
    mainEndpoints.optionSets,
    { params: { limit: CATALOGUE_PAGE_LIMIT, offset: 0 } },
  )

  return Array.isArray(page) ? page : (page.rows ?? [])
}

/** The API caps a page at 100, and a tenant's own categories fit well inside that. */
const CATALOGUE_PAGE_LIMIT = 100
